import { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import "@/App.css";
import { getToken, getUser } from "@/lib/api";
import LoginPage from "@/pages/Login";
import AdminLoginPage from "@/pages/AdminLogin";
import RegisterPage from "@/pages/Register";
import HomePage from "@/pages/HomePage";
import ContactPage from "@/pages/ContactPage";
import AboutPage from "@/pages/AboutPage";
import StudentDashboard from "@/pages/StudentDashboard";
import CourseCatalog from "@/pages/CourseCatalog";
import CourseDetails from "@/pages/CourseDetails";
import StudentAssignments from "@/pages/StudentAssignments";
import StudentQuizzes from "@/pages/StudentQuizzes";
import StudentAnalytics from "@/pages/StudentAnalytics";
import StudentGames from "@/pages/StudentGames";
import StudentCoding from "@/pages/StudentCoding";
import StudentMyClasses from "@/pages/StudentMyClasses";
import StudentAchievements from "@/pages/StudentAchievements";
import StaffDashboard from "@/pages/StaffDashboard";
import StaffCreate from "@/pages/StaffCreate";
import AdminDashboard from "@/pages/AdminDashboard";

function Protected({ roles, children }) {
  const token = getToken();
  const user = getUser();
  if (!token || !user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) {
    return <Navigate to={user.role === "admin" ? "/admin" : user.role === "staff" ? "/staff" : "/student"} replace />;
  }
  return children;
}

function RootRedirect() {
  const user = getUser();
  if (!user) return <HomePage />;
  if (user.role === "admin") return <Navigate to="/admin" replace />;
  if (user.role === "staff") return <Navigate to="/staff" replace />;
  return <Navigate to="/student" replace />;
}

export default function App() {
  return (
    <div className="App">
      <Toaster position="bottom-right" richColors />
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<RootRedirect />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/admin/login" element={<AdminLoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/home" element={<HomePage />} />

          {/* Student */}
          <Route path="/student" element={<Protected roles={["student"]}><StudentDashboard /></Protected>} />
          <Route path="/student/courses" element={<Protected roles={["student", "staff", "admin"]}><CourseCatalog /></Protected>} />
          <Route path="/student/enrolled" element={<Protected roles={["student"]}><CourseCatalog /></Protected>} />
          <Route path="/student/assignments" element={<Protected roles={["student"]}><StudentAssignments /></Protected>} />
          <Route path="/student/quizzes" element={<Protected roles={["student"]}><StudentQuizzes /></Protected>} />
          <Route path="/student/my-classes" element={<Protected roles={["student"]}><StudentMyClasses /></Protected>} />
          <Route path="/student/games" element={<Protected roles={["student", "staff", "admin"]}><StudentGames /></Protected>} />
          <Route path="/student/coding" element={<Protected roles={["student", "staff", "admin"]}><StudentCoding /></Protected>} />
          <Route path="/student/achievements" element={<Protected roles={["student"]}><StudentAchievements /></Protected>} />
          <Route path="/student/analytics" element={<Protected roles={["student"]}><StudentAnalytics /></Protected>} />

          {/* Shared */}
          <Route path="/courses/:id" element={<Protected><CourseDetails /></Protected>} />

          {/* Staff */}
          <Route path="/staff" element={<Protected roles={["staff"]}><StaffDashboard /></Protected>} />
          <Route path="/staff/courses" element={<Protected roles={["staff", "admin"]}><CourseCatalog /></Protected>} />
          <Route path="/staff/create" element={<Protected roles={["staff", "admin"]}><StaffCreate /></Protected>} />
          <Route path="/staff/students" element={<Protected roles={["staff", "admin"]}><StaffDashboard /></Protected>} />
          <Route path="/staff/analytics" element={<Protected roles={["staff", "admin"]}><StaffDashboard /></Protected>} />

          {/* Admin */}
          <Route path="/admin" element={<Protected roles={["admin"]}><AdminDashboard /></Protected>} />
          <Route path="/admin/users" element={<Protected roles={["admin"]}><AdminDashboard /></Protected>} />
          <Route path="/admin/courses" element={<Protected roles={["admin"]}><AdminDashboard /></Protected>} />
          <Route path="/admin/system" element={<Protected roles={["admin"]}><AdminDashboard /></Protected>} />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}
